import { Box } from '@mui/material';
import React, {
  ReactElement,
  ReactNode,
  SyntheticEvent,
  useCallback,
  useEffect,
  useState,
} from 'react';
import BookRideHeader from '../../bookRideHeader';
import { Controller, useFormContext } from 'react-hook-form';
import { BookRideFieldNames, TripTypes } from '../../types';

// 3 Booking Flows
import OneWayForm from './OneWayForm';
import RoundtripForm from './RoundtripForm';
import HourlyForm from './HourlyForm';

import { useProgramPassContext } from 'pages/ProgramPassContext';
import TabSystem from '../../../../components/shared/TabSystem';
import { COLORS } from 'theme/pallete';

const BookRideStep1 = (): ReactNode =>
{
  const { control, watch, setValue, clearErrors } = useFormContext() ?? {};
  const [tabs, setTabs] = useState<
    { label: string; value: string; content: ReactNode }[]
  >([]);
  const { programPass } = useProgramPassContext();

  // Pass Progam customizations
  if (programPass?.businessProgram?.buttonForeColor) {
    COLORS.BLACK_DARK_BTN = `#${programPass.businessProgram.buttonForeColor}`;
  }

  const tripType = watch('TRIP_TYPE');

  const createTabs = useCallback(() =>
  {
    const allowedOneWay =
      !programPass ||
      programPass.businessProgram.allowedTripTypes.includes(TripTypes.OneWay);
    const allowedRoundTrip =
      !programPass ||
      programPass.businessProgram.allowedTripTypes.includes(
        TripTypes.RoundTrip,
      );
    const allowedHourly =
      !programPass ||
      programPass.businessProgram.allowedTripTypes.includes(TripTypes.Hourly);

    const localTabs: {
      label: string;
      value: string;
      content: ReactNode;
      dataCy: string;
    }[] = [];
    if (allowedOneWay) {
      localTabs.push({
        label: 'One-way',
        value: '0',
        content: <OneWayForm key="tab 1" isGettingServiceProvider={false} />,
        dataCy: 'one-way-tab',
      });
    }
    if (allowedRoundTrip) {
      localTabs.push({
        label: 'Roundtrip',
        value: '1',
        content: <RoundtripForm key="tab 2" isGettingServiceProvider={false} />,
        dataCy: 'roundtrip-tab',
      });
    }
    if (allowedHourly) {
      localTabs.push({
        label: 'Hourly',
        value: '2',
        content: <HourlyForm key="tab 3" isGettingServiceProvider={false} />,
        dataCy: 'hourly-tab',
      });
    }

    setTabs(localTabs);

    if (!localTabs.find((t) => parseInt(t.value) === tripType))
      setValue('TRIP_TYPE', parseInt(localTabs[0].value));
  }, [programPass, setValue, tripType]);

  useEffect(() =>
  {
    createTabs();
  }, [createTabs]);

  return (
    <>
      <Box
        sx={{
          mb: '40px',
        }}
      >
        <BookRideHeader
          isPass={Boolean(programPass)}
          imageUrl={programPass?.businessProgram.heroImageUrl}
          imagePos={programPass?.businessProgram.heroImagePosition}
          title={programPass?.businessProgram.cmsTitle}
          description={programPass?.businessProgram.cmsSubtitle}
          informationMessage={programPass?.businessProgram.cmsInformationMessage}
          accountName={programPass?.businessProgram.businessAccountName}
        />
        <Box
          sx={{
            display: 'inline-block',
            mt: '27px',
            width: '100%',
          }}
        >
          <Controller
            name={BookRideFieldNames.TRIP_TYPE}
            control={control}
            render={({ field: { value, onChange } }): ReactElement => (
              <TabSystem
                value={value.toString() ?? ''}
                tabs={tabs}
                content={tabs.map((tab) => tab.content)}
                onChange={(
                  _: SyntheticEvent<Element, Event>,
                  option: unknown,
                ): void =>
                {
                  clearErrors();
                  onChange(parseInt(option as string));
                }}
                tabsSx={{
                  width: '380px',
                  mb: '40px',
                }}
              />
            )}
          />
        </Box>
      </Box>
    </>
  );
};

export default BookRideStep1;
